import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Check, Loader2, Send, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';
import { DateStamp, EmptyState, QueryError, Status, SurfaceHead } from '@/workspace/ui';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api/integrations/meta/sendback`;

async function call(path, init) {
  const res = await fetch(`${API}${path}`, { headers: { 'Content-Type': 'application/json' }, ...init });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Request failed (${res.status})`);
  return data;
}

/** Send-back — what Tether tells the ad platforms: every attributed purchase (and first identification) to Meta's
 *  Conversions API with the click id, hashed identity and order id, so Meta credits the right ad and dedupes against
 *  the pixel. One switch; no automation to build. */
export default function SendbackPage() {
  const qc = useQueryClient();
  const cfg = useQuery({ queryKey: ['sendback'], queryFn: () => call('') });
  const runs = useQuery({ queryKey: ['sendback-runs'], queryFn: () => call('/runs?limit=50'), refetchInterval: 15000 });
  const c = cfg.data;
  const [pixel, setPixel] = useState('');
  const [token, setToken] = useState('');
  const [testCode, setTestCode] = useState('');
  const [events, setEvents] = useState({ purchase: true, lead: false, renewal: false });
  const [combine, setCombine] = useState(0);
  const [busy, setBusy] = useState('');
  const dirty = useRef(false);
  const pending = useRef(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!c || dirty.current) return;
    setPixel(c.pixel_id || '');
    setTestCode(c.test_event_code || '');
    setEvents(c.events || { purchase: true, lead: false, renewal: false });
    setCombine(c.combine_window || 0);
  }, [c]);

  const run = async (which, fn, ok) => {
    if (pending.current) return null;
    pending.current = true;
    setBusy(which);
    setError('');
    try {
      const r = await fn();
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['sendback'] }),
        qc.invalidateQueries({ queryKey: ['sendback-runs'] }),
      ]);
      if (ok) toast.success(ok);
      return r;
    } catch (e) {
      const message = e.message || 'Request failed. Please try again.';
      setError(message);
      toast.error(message);
      return null;
    } finally {
      pending.current = false;
      setBusy('');
    }
  };
  const save = () => {
    if (pending.current) return;
    const message = !/^\d+$/.test(pixel.trim()) ? 'Pixel ID must be numeric.'
      : !Number.isFinite(Number(combine)) || Number(combine) < 0 || Number(combine) > 120 ? 'Combine window must be between 0 and 120 minutes.'
        : !token.trim() && !c?.configured ? 'Add a Conversions API access token.' : '';
    if (message) { setError(message); toast.error(message); return; }
    return run('save', () => call('', { method: 'PUT', body: JSON.stringify({ pixel_id: pixel.trim(), access_token: token || undefined, test_event_code: testCode, events, combine_window: Number(combine) }) }), 'Saved').then(result => { if (result) setToken(''); });
  };
  const toggle = () => {
    if (pending.current || !c?.configured || (!c.enabled && configurationChanged)) return;
    const enabled = !c.enabled;
    const selected = [['purchase', 'Purchases'], ['lead', 'First identification'], ['renewal', 'Renewals']].filter(([key]) => c.events?.[key]).map(([, label]) => label).join(', ') || 'no events';
    const message = enabled
      ? `Switch on automatic send-back to saved pixel ${c.pixel_id} for ${selected}? Only saved settings will be used.`
      : 'Pause automatic send-back? New events will not be sent while paused. Existing deliveries are kept.';
    if (!window.confirm(message)) return;
    return run('toggle', () => call('', { method: 'PUT', body: JSON.stringify({ enabled }) }), enabled ? 'Send-back is on for your saved event selections' : 'Send-back paused');
  };
  const configurationChanged = pixel.trim() !== (c?.pixel_id || '') || testCode.trim() !== (c?.test_event_code || '') || !!token.trim() || Number(combine) !== (c?.combine_window || 0) || ['purchase', 'lead', 'renewal'].some(k => !!events[k] !== !!c?.events?.[k]);
  const canTest = !!c?.configured && !!c?.test_event_code && !configurationChanged;
  const test = () => canTest && run('test', async () => { const r = await call('/test', { method: 'POST' }); if (!r.success) throw new Error(r.error || r.response || `Meta answered ${r.http_status}`); return r; }, 'Test event sent — check Events Manager → Test events');

  return <>
    <PageHeader title="Send-back" />
    <div className="sp-sendback-grid">
      <section className="panel">
        <SurfaceHead title="Meta Conversions API">{c?.configured && <Status tone={c.enabled ? 'green' : 'quiet'}>{c.enabled ? 'Sending' : 'Paused'}</Status>}</SurfaceHead>
        {cfg.isError ? <QueryError onRetry={cfg.refetch} /> : cfg.isLoading ? <div className="empty-state">Loading configuration…</div> : <form aria-label="Send-back configuration" className="sp-configuration-body" onSubmit={e => { e.preventDefault(); save(); }}>
          {c?.configured && <div className="sp-connection-status"><div><strong>{c.enabled ? 'Automatic send-back is on' : 'Automatic send-back is paused'}</strong><p>Stored token: {c.token || 'Configured'}</p></div><button type="button" className="btn" onClick={toggle} aria-busy={busy === 'toggle'} disabled={!!busy || (!c.enabled && configurationChanged)}>{busy === 'toggle' && <Loader2 size={14} className="sp-spin" />}{c.enabled ? 'Pause sending' : 'Switch on'}</button></div>}
          {error && <p className="sp-form-error" role="alert">{error}</p>}
          {c?.stats?.last_error && <p className="sp-form-error" role="alert">Last error: {c.stats.last_error}</p>}
          <div className="sp-form-grid">
            <label className="sp-config-field">Pixel ID<input disabled={!!busy} className="sp-input" value={pixel} onChange={e => { dirty.current = true; setPixel(e.target.value); }} placeholder="123456789012345" data-testid="sendback-pixel" /></label>
            <label className="sp-config-field">Test event code <span>(optional)</span><input disabled={!!busy} className="sp-input" value={testCode} onChange={e => { dirty.current = true; setTestCode(e.target.value); }} placeholder="TEST12345" /></label>
          </div>
          <label className="sp-config-field">Conversions API access token<input disabled={!!busy} className="sp-input" value={token} onChange={e => { dirty.current = true; setToken(e.target.value); }} type="password" autoComplete="off" placeholder={c?.configured ? 'Leave empty to keep the stored token' : 'EAAB…'} data-testid="sendback-token" /></label>
          <fieldset className="sp-event-options"><legend>Events to send</legend>
            {[['purchase', 'Purchases'], ['lead', 'First identification'], ['renewal', 'Renewals']].map(([k, label]) => <label key={k} data-checked={!!events[k]}><input disabled={!!busy} type="checkbox" checked={!!events[k]} onChange={e => { dirty.current = true; setEvents({ ...events, [k]: e.target.checked }); }} />{label}</label>)}
          </fieldset>
          <label className="sp-config-field">Combine window<div className="sp-combine-field"><input disabled={!!busy} className="sp-input" type="number" min={0} max={120} value={combine} onChange={e => { dirty.current = true; setCombine(e.target.value); }} /><span>minutes</span></div><small>Merge upsells within this window into one Purchase event.</small></label>
          <div className="sp-form-actions"><button type="submit" aria-busy={busy === 'save'} disabled={!!busy || !pixel.trim() || (!token && !c?.configured)} className="btn primary" data-testid="sendback-save">{busy === 'save' && <Loader2 size={14} className="sp-spin" />}Save configuration</button><button type="button" onClick={test} aria-busy={busy === 'test'} disabled={!!busy || !canTest} className="btn">{busy === 'test' ? <Loader2 size={14} className="sp-spin" /> : <Send size={14} />}Send test event</button></div>
          <p className="text-[12px]" style={{ color: 'var(--text-muted)' }}>Save configuration with a test event code before sending a test. The test sends a synthetic Lead to the saved pixel, not your unsaved draft.</p>
        </form>}
      </section>
    </div>
    <section className="panel sp-deliveries">
      <SurfaceHead title="Recent deliveries" />
      {runs.isError ? <QueryError onRetry={runs.refetch} /> : runs.isLoading ? <div className="empty-state">Loading deliveries…</div> : !runs.data?.length ? <EmptyState icon={Send} title="No deliveries yet" description="Attributed events will appear here once send-back is configured and switched on." /> : <div className="sp-delivery-list">
        {runs.data.map(r => <div className="sp-delivery-row" key={r.id}>
          <span className="sp-delivery-icon" data-success={!!r.success}>{r.success ? <Check size={16} /> : <XCircle size={16} />}</span>
          <div><strong>{r.action_name?.replace('Send-back · ', '') || 'Conversion event'}</strong><p>{r.contact_email || r.contact_name || r.contact_id || 'No linked identity'}</p></div>
          <Status tone={r.success ? 'green' : 'red'}>{r.success ? 'Delivered' : 'Failed'}{r.http_status ? ' · ' + r.http_status : ''}</Status>
          <Status>{r.run_type || 'live'}</Status><DateStamp value={r.triggered_at} />
        </div>)}
      </div>}
    </section>
    <div className="sp-coming-next"><div><strong>Google offline conversions</strong><p>Upload attributed sales with gclid; refunds become adjustments.</p></div><Status>Not yet available</Status></div>
  </>;
}
